"use client";

import { useLang } from "@/contexts/language-context";
import AnimatedSection, { AnimatedElement } from "./AnimatedSection";
import AppStoreBadges from "./AppStoreBadges";
import { Info, UserPlus, Tablet, PlayCircle } from "lucide-react";

export default function HardwareSection() {
  const { t } = useLang();

  const steps = [
    {
      icon: <UserPlus className="w-6 h-6 text-redy-charcoal" />,
      title: { en: "Create your Account", es: "Crea tu Cuenta" },
      desc: { en: "We'll guide you through payments onboarding and ordering your North payment terminal. Ships directly to you.", es: "Te guiamos con la aprobación de tu cuenta de pagos y para ordenar tu terminal North. Llega directo a ti." }
    },
    {
      icon: <Tablet className="w-6 h-6 text-redy-charcoal" />,
      title: { en: "Set up your iPad", es: "Configura tu iPad" },
      desc: { en: "Load your catalog and team. Use any iPad or Android tablet you already own.", es: "Carga tu catálogo y tu equipo. Usa cualquier iPad o Android que ya tengas." }
    },
    {
      icon: <PlayCircle className="w-6 h-6 text-redy-charcoal" />,
      title: { en: "Start Taking Orders", es: "Toma Órdenes" },
      desc: { en: "Once your payments account is approved, connect your terminal, run a test, and you're live with dual pricing built in.", es: "Cuando se aprueba tu cuenta de pagos, conecta tu terminal, haz una prueba y empieza a cobrar con dual pricing incluido." }
    }
  ];

  return (
    <section className="py-32 px-6 bg-[#FAFAFA] overflow-hidden">
      <div className="max-w-[1200px] mx-auto text-center">
        <AnimatedSection>
          <div className="text-[11px] font-bold tracking-widest uppercase text-redy-orange mb-4">
            {t("Getting started", "Para empezar")}
          </div>
          <h2 className="text-4xl md:text-5xl font-black tracking-tight leading-[1.1] text-redy-charcoal mb-4">
            {t("How Our Setup", "Cómo Nuestra Configuración")}
            <br />
            <span className="text-black/30">
              {t("Experience Works", "Funciona")}
            </span>
          </h2>
          <p className="text-[15px] font-medium text-redy-secondary max-w-2xl mx-auto mb-20">
            {t("One terminal. Your iPad. That's the whole setup. No expensive hardware bundles.", "Un terminal. Tu iPad. Eso es todo. Sin paquetes de hardware caros.")}
          </p>
        </AnimatedSection>

        <div className="relative max-w-[1000px] mx-auto">
          {/* Curved connecting line */}
          <div className="hidden md:block absolute top-[40px] left-[10%] right-[10%] h-[150px] border-t-2 border-dashed border-black/10 rounded-t-[100px] -z-10" />
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-16 relative z-10">
            {steps.map((step, i) => (
              <AnimatedElement key={i} delay={0.1 * i} className="flex flex-col items-center">
                <div className="w-[80px] h-[80px] bg-white rounded-full flex items-center justify-center shadow-lg border border-black/5 mb-6 relative group">
                  {step.icon}
                  {/* Small decorative dot */}
                  <div className="absolute top-0 right-0 w-3 h-3 bg-redy-orange rounded-full border-2 border-white group-hover:scale-150 transition-transform" />
                </div>
                <h3 className="text-[18px] font-bold text-redy-charcoal mb-3">{t(step.title.en, step.title.es)}</h3>
                <p className="text-[14px] font-medium text-redy-secondary leading-relaxed">{t(step.desc.en, step.desc.es)}</p>
              </AnimatedElement>
            ))}
          </div>
        </div>

        <AnimatedSection delay={0.4} className="mt-20 p-6 bg-white rounded-[24px] shadow-sm border border-black/5 flex gap-5 items-start max-w-3xl mx-auto text-left">
          <div className="w-12 h-12 rounded-full bg-[#FAFAFA] border border-black/5 flex items-center justify-center shrink-0">
            <Info className="w-5 h-5 text-redy-charcoal" />
          </div>
          <div>
            <div className="text-[14px] font-bold text-redy-charcoal mb-1">
              {t("About the North Terminal", "Sobre el Terminal North")}
            </div>
            <p className="text-[14px] font-medium text-redy-secondary leading-relaxed">
              {t(
                "It's a professional-grade card reader that handles tap, chip, and swipe. You purchase it once — it's yours. No monthly rental fees, no returning it if you leave.",
                "Es un lector de tarjetas profesional (tap, chip, swipe). Lo compras una vez y es tuyo. Sin cuotas mensuales de renta, y no tienes que devolverlo si te vas."
              )}
            </p>
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.5} className="mt-10 flex justify-center">
          <button
            onClick={() => document.getElementById("register")?.scrollIntoView({ behavior: "smooth" })}
            className="inline-flex items-center gap-2 text-[14px] font-bold text-white bg-redy-charcoal px-8 py-3.5 rounded-full hover:bg-black transition-all active:scale-95 shadow-sm cursor-pointer"
          >
            {t("Create your account", "Crea tu cuenta")}
            <span className="text-lg leading-none">→</span>
          </button>
        </AnimatedSection>

        <AnimatedSection delay={0.6} className="mt-12 flex flex-col items-center gap-4">
          <div className="text-[11px] font-bold tracking-widest uppercase text-redy-secondary/60">
            {t(
              "Redy POS — now on the App Store & Google Play",
              "Redy POS — ya en App Store y Google Play"
            )}
          </div>
          <AppStoreBadges className="justify-center" height={44} />
        </AnimatedSection>
      </div>
    </section>
  );
}
